import { useCookie } from 'nuxt/app'

type CookieMap = {
	'authentication-cookie': AuthCookie
	'hide-download-app': boolean
	'metrics_url': string
	'lead-id': string
	'trip-invitation': { tripId: string; code: string }
	'sales-lead-id': string
}

export type AuthCookie = {
	auth: {
		accessToken: string
		id: string
	}
}

export default class CookieService {
	public static getCookie<K extends keyof CookieMap>(key: K): CookieMap[K] | undefined {
		return useCookie<CookieMap[K]>(key).value ?? undefined
	}

	public static deleteCookie<K extends keyof CookieMap>(key: K): void {
		useCookie(key).value = null
	}

	public static getAuthCookie(): AuthCookie | undefined {
		return this.getCookie('authentication-cookie')
	}

	public static setAuthCookie(accessToken: string, userId?: string, expirationDate?: Date): void {
		const authCookie = { auth: { id: userId, accessToken } }
		this.setCookie('authentication-cookie', authCookie as AuthCookie, expirationDate)
	}

	public static deleteAuthCookie(): void {
		this.deleteCookie('authentication-cookie')
	}

	public static setHideDownloadApp(hide: boolean): void {
		this.setCookie('hide-download-app', hide)
	}

	public static getRegisterMetricsUrl(): string | undefined {
		return this.getCookie('metrics_url')
	}

	public static setRegisterMetricsUrl(url: string): void {
		this.setCookie('metrics_url', url)
	}

	public static deleteRegisterMetricsUrl(): void {
		this.deleteCookie('metrics_url')
	}

	public static getLeadId(): string | undefined {
		return this.getCookie('lead-id')
	}

	public static setLeadId(leadId: string): void {
		this.setCookie('lead-id', leadId)
	}

	public static deleteLeadId(): void {
		this.deleteCookie('lead-id')
	}

	public static getSalesLeadId(): string | undefined {
		return this.getCookie('sales-lead-id')
	}

	public static setSalesLeadId(SalesleadId: string): void {
		this.setCookie('sales-lead-id', SalesleadId)
	}

	public static deleteSalesLeadId(): void {
		this.deleteCookie('sales-lead-id')
	}

	public static getTripInvitation(): { tripId: string; code: string } | undefined {
		return this.getCookie('trip-invitation')
	}

	public static setTripInvitation(invitation: { tripId: string; code: string }): void {
		this.setCookie('trip-invitation', invitation)
	}

	public static deleteTripInvitation(): void {
		this.deleteCookie('trip-invitation')
	}

	public static setAccepted(cookie: string, accepted: boolean, expirationDays: number): void {
		const expires = new Date()
		expires.setDate(expires.getDate() + expirationDays)
		this.setCookie(cookie as any, accepted, expires)
	}

	private static setCookie<K extends keyof CookieMap>(
		key: K,
		value: CookieMap[K],
		expirationDate?: Date
	): void {
		let expires = expirationDate
		if (!expires) expires = this.getDefaultExpiration()
		useCookie(key, { expires }).value = value
	}

	private static getDefaultExpiration(): Date {
		const oneMonthMilliseconds = 30 * 24 * 60 * 60 * 1000
		const now = new Date()
		const oneMonthFromNow = now.getTime() + oneMonthMilliseconds
		return new Date(oneMonthFromNow)
	}
}
